import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows } from '../../constants';
import { useAuth } from '../../context/AuthContext';
import { getUserDisplayName } from '../../utils/queryDisplayUtils';
import { formatDateTime } from '../../utils/dateUtils';

// Hooks
import { useQueryModalState } from '../../hooks/queries/useQueryModalState';
import { useQueryResponse } from '../../hooks/queries/useQueryResponse';
import { useQueryTimeline } from '../../hooks/queries/useQueryTimeline';
import { useQueryAssignment } from '../../hooks/queries/useQueryAssignment';
import { useQueryDrafts } from '../../hooks/queries/useQueryDrafts';
import { useAttachmentDialog } from '../../hooks/queries/useAttachmentDialog';
import { useQueryStatusUpdate } from '../../hooks/queries/useQueryStatusUpdate';
import { useQueryPermissions } from '../../hooks/queries/useQueryPermissions';
import { useProjectsCatalog } from '../../hooks/useProjectsCatalog';

// Components
import QueryModalHeader from './viewQueryModal/QueryModalHeader';
import QueryInfoSection from './viewQueryModal/QueryInfoSection';
import AssignmentInfoSection from './viewQueryModal/AssignmentInfoSection';
import EscalationInfoSection from './viewQueryModal/EscalationInfoSection';
import ConversationThread from './viewQueryModal/ConversationThread';
import ResponseForm from './viewQueryModal/ResponseForm';
import ActivityTimeline from './viewQueryModal/ActivityTimeline';
import DelegateDialog from './viewQueryModal/DelegateDialog';
import AttachmentViewDialog from './viewQueryModal/AttachmentViewDialog';
import SectionCard from './viewQueryModal/SectionCard';

const ViewQueryModal = ({ open, onClose, queryId, query: initialQuery, onEdit, onDelete, onQueryUpdated, onDelegated }) => {
  const { user } = useAuth();
  const location = useLocation();
  const isFromEscalatedRoute = location.pathname.includes('/escalated');
  const [conversationHistoryExpanded, setConversationHistoryExpanded] = useState(true);
  const resolvedQueryId = queryId || initialQuery?._id || initialQuery?.queryId;
  const projectsCatalog = useProjectsCatalog(open);

  // Timeline hook (needs to be initialized before query state)
  const {
    timelineEvents,
    timelineCounts,
    timelineLoading,
    timelineError,
    fetchTimeline,
  } = useQueryTimeline(resolvedQueryId);

  // Query state
  const {
    query,
    loading,
    updateQuery,
    fetchQuery,
  } = useQueryModalState({
    open,
    queryId: resolvedQueryId,
    initialQuery,
    onClose,
    onQueryFetched: fetchTimeline,
    onQueryUpdated,
  });


  // Response state - initialized with canRespond=false, will be updated after permissions are calculated
  const [canRespondState, setCanRespondState] = useState(false);

  const {
    responseMessage,
    setResponseMessage,
    attachments,
    setAttachments,
    isSubmittingResponse,
    isSavingDraft,
    setIsSavingDraft,
    responseError,
    setResponseError,
    isDragging,
    fileInputRef,
    handleFileSelect,
    handleAttachmentChange,
    handleRemoveAttachment,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleResponseSubmit,
    clearResponseState,
  } = useQueryResponse(
    resolvedQueryId,
    open,
    canRespondState,
    updateQuery,
    fetchTimeline
  );

  // Drafts (server-side)
  const {
    isSendingDraft,
    getDraftsFromThread,
    deleteDraft,
    sendDraft,
    handleSaveDraft,
    resetDraftState,
  } = useQueryDrafts(resolvedQueryId, open);

  // Attachment dialog
  const {
    attachmentDialogOpen,
    selectedAttachment,
    handleAttachmentClick,
    handleCloseAttachmentDialog,
    handleDownloadAttachment,
  } = useAttachmentDialog();

  // Assignment
  const {
    assignDialogOpen,
    assignTarget,
    assigningId,
    assignUsers,
    assignAllUsers,
    assignRegion,
    selectedAssigneeId,
    setSelectedAssigneeId,
    loadingAssignUsers,
    assignReason,
    setAssignReason,
    assignNote,
    setAssignNote,
    handleOpenAssignDialog,
    handleCloseAssignDialog,
    handleConfirmAssign,
    handleAssignRegionChange,
  } = useQueryAssignment(updateQuery, fetchTimeline, onDelegated, projectsCatalog);

  // Status update - initialized with canUpdateStatus=false, will be updated after permissions are calculated
  const [canUpdateStatusState, setCanUpdateStatusState] = useState(false);

  const {
    selectedStatus,
    setSelectedStatus,
    statusError,
    setStatusError,
    isUpdatingStatus,
    statusOptions,
    normalizedCurrentStatus,
    handleStatusUpdate,
  } = useQueryStatusUpdate(
    query,
    resolvedQueryId,
    canUpdateStatusState,
    updateQuery,
    fetchTimeline
  );

  // Sorted thread (exclude drafts from the public thread)
  const sortedThread = useMemo(() => {
    if (!query?.thread) return [];
    return [...query.thread]
      .filter((t) => !t.isDraft)
      .sort((a, b) => {
        const aTime = a.sentAt ? new Date(a.sentAt).getTime() : 0;
        const bTime = b.sentAt ? new Date(b.sentAt).getTime() : 0;
        return bTime - aTime;
      });
  }, [query?.thread]);

  // Derive saved drafts from server-side thread entries
  const savedDrafts = useMemo(() => {
    return getDraftsFromThread(query?.thread, user?._id || user?.id);
  }, [query?.thread, user, getDraftsFromThread]);

  // Permissions
  const permissions = useQueryPermissions(user, query, sortedThread);
  const {
    hasRespondAccess,
    canRespond,
    canUpdateStatus,
    isOriginator,
    isThreadDisabledForOriginator,
    isEscalatedViewOnly,
    canSeeStatusSection,
    normalizedUserRole,
    normalizedCurrentStatus: permNormalizedCurrentStatus,
    isSubmittedStatus,
    isEscalated,
    isClosed,
  } = permissions;

  // Update canRespond and canUpdateStatus states when permissions change
  useEffect(() => {
    setCanRespondState(canRespond);
    setCanUpdateStatusState(canUpdateStatus);
  }, [canRespond, canUpdateStatus]);

  // Escalation info
  const escalatedToName = isEscalated ? getUserDisplayName(query?.escalatedTo) : '';
  const escalatedByName = isEscalated ? getUserDisplayName(query?.escalatedBy) : '';
  const escalatedAt =
    isEscalated && (query?.escalatedAt || query?.escalationDate || query?.updatedAt)
      ? query?.escalatedAt || query?.escalationDate || query?.updatedAt
      : null;


  // Fetch timeline when query is first loaded (only once per query)
  const queryIdRef = useRef(null);
  useEffect(() => {
    if (query && open && query._id !== queryIdRef.current) {
      queryIdRef.current = query._id;
      fetchTimeline();
    }
    if (!open) {
      queryIdRef.current = null;
    }
  }, [query?._id, open, fetchTimeline]);

  // Clear response state when modal closes
  useEffect(() => {
    if (!open) {
      clearResponseState();
      resetDraftState();
    }
  }, [open, clearResponseState, resetDraftState]);

  // Handle send draft response (converts server-side draft to final)
  const handleSendDraftResponse = async (draft) => {
    await sendDraft(draft, resolvedQueryId, canRespond, updateQuery, fetchTimeline);
  };

  // Handle delete draft (server-side)
  const handleDeleteDraft = async (draftThreadId) => {
    const updatedQuery = await deleteDraft(draftThreadId);
    if (updatedQuery) {
      updateQuery(updatedQuery);
      fetchTimeline({ silent: true });
    }
  };

  // Handle load draft — loads draft message into the response form
  const handleLoadDraft = (draft) => {
    setResponseMessage(draft.message);
    setAttachments([]);
  };

  // Wrap handleSaveDraft to refresh query data after saving
  const onSaveDraftHandler = handleSaveDraft(
    responseMessage,
    attachments,
    canRespond,
    setIsSavingDraft,
    setResponseError,
    setResponseMessage,
    setAttachments,
    fileInputRef
  );

  const handleSaveDraftAndRefresh = async (event) => {
    const updatedQuery = await onSaveDraftHandler(event);
    if (updatedQuery) {
      updateQuery(updatedQuery);
      fetchTimeline({ silent: true });
    }
  };

  if (!open) return null;

  return (
    <>
      <style>
        {`
          .view-query-modal input:not(:focus):hover,
          .view-query-modal select:not(:focus):hover,
          .view-query-modal textarea:not(:focus):hover {
            border-color: ${colors.borderHover} !important;
          }
        `}
      </style>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        className="view-query-modal"
        PaperProps={{
          sx: {
            borderRadius: borderRadius.modal,
            boxShadow: shadows.xl,
            maxHeight: '95vh',
            overflow: 'hidden',
          },
        }}
      >
        <QueryModalHeader
          query={query}
          isEscalated={isEscalated}
          escalatedToName={escalatedToName}
          isEscalatedViewOnly={isEscalatedViewOnly}
          isClosed={isClosed}
          onEdit={onEdit}
          onDelete={onDelete}
          onClose={onClose}
        />

        <DialogContent
          sx={{
            padding: spacing.md,
            overflowY: 'auto',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : query ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              <QueryInfoSection
                query={query}
                projectsCatalog={projectsCatalog}
                onAttachmentClick={handleAttachmentClick}
              />

              <AssignmentInfoSection
                query={query}
                hasRespondAccess={hasRespondAccess}
                isEscalatedViewOnly={isEscalatedViewOnly}
                isSubmittedStatus={isSubmittedStatus}
                isClosed={isClosed}
                normalizedUserRole={normalizedUserRole}
                isFromEscalatedRoute={isFromEscalatedRoute}
                onDelegateClick={handleOpenAssignDialog}
                assigningId={assigningId}
              />

              {isEscalated && (
                <EscalationInfoSection
                  query={query}
                  isEscalated={isEscalated}
                  escalatedByName={escalatedByName}
                  escalatedToName={escalatedToName}
                  escalatedAt={escalatedAt}
                  isEscalatedViewOnly={isEscalatedViewOnly}
                />
              )}

              {!isFromEscalatedRoute && (
                <SectionCard title="Conversation Thread">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                    <ResponseForm
                      responseMessage={responseMessage}
                      attachments={attachments}
                      isDragging={isDragging}
                      canRespond={canRespond}
                      isSubmittingResponse={isSubmittingResponse}
                      isSavingDraft={isSavingDraft}
                      responseError={responseError}
                      isSubmittedStatus={isSubmittedStatus}
                      isClosed={isClosed}
                      isEscalatedViewOnly={isEscalatedViewOnly}
                      isThreadDisabledForOriginator={isThreadDisabledForOriginator}
                      hasRespondAccess={hasRespondAccess}
                      onResponseChange={(e) => {
                        setResponseMessage(e.target.value);
                        if (responseError) {
                          setResponseError('');
                        }
                      }}
                      onFileSelect={handleAttachmentChange}
                      onFileRemove={handleRemoveAttachment}
                      onDragHandlers={{
                        onDragOver: handleDragOver,
                        onDragLeave: handleDragLeave,
                        onDrop: handleDrop,
                      }}
                      onSaveDraft={handleSaveDraftAndRefresh}
                      onSubmit={handleResponseSubmit}
                      fileInputRef={fileInputRef}
                    />

                    <Box sx={{ pt: spacing.sm, borderTop: `1px solid ${colors.borderLight}` }}>
                      <Box
                        onClick={() => setConversationHistoryExpanded(!conversationHistoryExpanded)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          mb: conversationHistoryExpanded ? spacing.sm : 0,
                          '&:hover': {
                            opacity: 0.8,
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: fonts.weightSemiBold,
                            color: colors.textPrimaryDark,
                            fontSize: fonts.fontSizeSm,
                          }}
                        >
                          Conversation History
                        </Typography>
                        {conversationHistoryExpanded ? (
                          <ExpandLessIcon sx={{ color: colors.textSecondary, fontSize: '20px' }} />
                        ) : (
                          <ExpandMoreIcon sx={{ color: colors.textSecondary, fontSize: '20px' }} />
                        )}
                      </Box>
                      <Collapse in={conversationHistoryExpanded}>
                        <ConversationThread
                          sortedThread={sortedThread}
                          savedDrafts={savedDrafts}
                          user={user}
                          handleAttachmentClick={handleAttachmentClick}
                          handleSendDraftResponse={handleSendDraftResponse}
                          handleDeleteDraft={handleDeleteDraft}
                          handleLoadDraft={handleLoadDraft}
                          isSubmittingResponse={isSubmittingResponse}
                          isSavingDraft={isSavingDraft}
                          isSendingDraft={isSendingDraft}
                        />
                      </Collapse>
                    </Box>
                  </Box>
                </SectionCard>
              )}

              <SectionCard title="Activity Timeline">
                <ActivityTimeline
                  timelineEvents={timelineEvents}
                  timelineCounts={timelineCounts}
                  timelineLoading={timelineLoading}
                  timelineError={timelineError}
                  handleAttachmentClick={handleAttachmentClick}
                />
              </SectionCard>
            </Box>
          ) : (
            <Typography sx={{ textAlign: 'center', py: spacing.xl, color: colors.textSecondary }}>
              No query data available
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: `${spacing.xs} ${spacing.sm}`,
            borderTop: 'none',
            background: 'linear-gradient(135deg, #ffffff 0%, #eff4ff 100%)',
            position: 'sticky',
            bottom: 0,
            gap: spacing.xs,
            justifyContent: 'flex-end',
            boxShadow: '0 -12px 28px rgba(14, 23, 42, 0.12)',
          }}
        >
          {/* Footer actions can be added here if needed */}
        </DialogActions>
      </Dialog>

      <DelegateDialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        query={assignTarget}
        assignUsers={assignUsers}
        assignAllUsers={assignAllUsers}
        assignRegion={assignRegion}
        selectedAssigneeId={selectedAssigneeId}
        loadingAssignUsers={loadingAssignUsers}
        assignReason={assignReason}
        assignNote={assignNote}
        assigningId={assigningId}
        onRegionChange={handleAssignRegionChange}
        onAssigneeChange={(e) => setSelectedAssigneeId(e.target.value)}
        onReasonChange={(e) => setAssignReason(e.target.value)}
        onNoteChange={(e) => setAssignNote(e.target.value)}
        onConfirm={handleConfirmAssign}
        onCancel={handleCloseAssignDialog}
      />

      <AttachmentViewDialog
        open={attachmentDialogOpen}
        onClose={handleCloseAttachmentDialog}
        attachment={selectedAttachment}
        onDownload={handleDownloadAttachment}
      />
    </>
  );
};

export default ViewQueryModal;
